import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { Frame } from '../components/Frame';
import { KrabenMark } from '../components/KrabenMark';
import { loadProfile, saveProfile } from '../lib/state';
import { track } from '../analytics';
import { PANELS, BECOMES, WHEN, KRABEN_HELLO, DOOR_QUOTE, QUESTION, DREAM, DREAMS, dreamForIdea, ideaOf } from '../data/intro-copy';
import { getDoor, type DoorDef } from '../data/doors';
import { loadCampaign } from '../lib/campaign';
import { claimPreDoor, finishPreDoor } from '../lib/pre-door';
import { claimPreQuiz, finishPreQuiz } from '../lib/pre-quiz';
import { claimPreRoad, finishPreRoad } from '../lib/pre-road';
import { preloadClip } from '../lib/clip';
import { currentStarter, startPath } from '../lib/starter';
import { FirstRoad } from '../components/FirstRoad';
import { KindStep, AboutStep } from '../components/IdeaSteps';
import { ideaStep, landing } from '../lib/experiments';
import { useViewed } from '../lib/viewed';

/**
 * The front door, Day 1.
 *
 *  1. The adult door: one quote over a full-screen still of a grown-up's desk.
 *     Tap plays the clip (made on the AUX board) that carries the desk into
 *     the workshop; when it ends, the survey. Skip is always there.
 *     `/?door=plush` leaves it out entirely.
 *  2. The survey, in the workshop look: one question, no introduction from
 *     anybody. The reply is the reward. The options are big and there is no
 *     button until one is chosen.
 *  3. The dream: what they would like to be, as many as fit. Naming it starts
 *     the identity; Day 2's exercise puts it on a badge.
 *  3b. For the imagine starter's people (the `idea_step` experiment, decided
 *     on, lib/experiments.ts, well/product/33 §8), when they picked a dream
 *     other than "Not sure yet": what kind of thing they want to make (chips,
 *     one tap), then their last great idea (a box, where they think), or "I
 *     don't have any", which goes straight to the Postcard Machine to spin one
 *     (components/IdeaSteps.tsx, owner 2026-10-19). With an idea, the starter
 *     skips the place: the crew draw them in it.
 *  4. Three cards: the offer, where it goes, and the one that says an exercise
 *     a day with the first starting now (one line of big text, no paragraph).
 *     With `?start=imagine`, the first card only, then the road, with today's
 *     stop their first creation (components/FirstRoad.tsx, owner 2026-10-15).
 *  5. Kraben's moment — a clip of him handing over the first exercise ("tell a
 *     story, just start talking"), not introducing himself — then the warm-up.
 *
 * In the `landing` experiment's `road` arm (lib/experiments.ts, owner
 * 2026-10-19), none of this: the ad visitor opens on the road itself, headed
 * by the ad's line, and its one button goes straight into the first creation
 * (components/FirstRoad.tsx with `promise`). index.html has usually drawn it
 * already (lib/pre-road.ts).
 *
 * The words AI and film stay out of these screens (the brief); the reward
 * screen after the first card is where the road is shown.
 */
type Screen = 'landing' | 'door' | 'survey' | 'dream' | 'kind' | 'about' | 'panels' | 'road' | 'kraben';

/** How long the door's clip runs before Skip appears. */
const SKIP_AFTER_MS = 1400;

/** How long the last frame of Kraben's clip is held, offering another look. */
const REPLAY_GRACE_MS = 4500;

function firstScreen(): Screen {
  // index.html only gives the landing an arm for the imagine starter with no door; the starter is checked again here all the same
  if (landing() === 'road' && currentStarter() === 'imagine') return 'landing';
  return new URLSearchParams(window.location.search).get('door') === 'plush' ? 'survey' : 'door';
}

/**
 * The door: the picture the ad was made of, the quote, and the ad's own line.
 *
 * It was built on the assumption that it is skipped (17 §8, owner 2026-09-18);
 * in fact people do watch the clip, but take a moment to see that the door
 * wants a tap (owner, 2026-10-08). The poster is still what somebody sees
 * first, and the promise is repeated on the question screen. A door with no
 * clip shows no play affordance at all and any tap moves on: a "Tap to play"
 * over a still that cannot play is worse than no clip.
 *
 * index.html has usually drawn this door already (lib/pre-door.ts). Then this
 * one takes that door's <video> or <img> rather than making its own, so the
 * poster never reloads and a clip started before the app was here plays on.
 */
function AdultDoor({ door, onDone }: { door: DoorDef; onDone: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [pre] = useState(() => claimPreDoor(door.slug));
  const slot = useRef<HTMLDivElement>(null);
  const [playing, setPlaying] = useState(() => !!door.clip && pre?.firstTapAt != null);
  // asked to play and actually moving are two moments on a slow connection: in
  // between, the button stays and says so, instead of fading to a still frame
  const [moving, setMoving] = useState(() => !!pre?.moving);
  const [leaving, setLeaving] = useState(false);
  // Skip is an escape hatch, not an invitation. On the first frame it is the
  // only word shaped like a button, and it tells somebody the thing they are
  // looking at is not worth watching before they have seen any of it (owner,
  // interview 2026-09-18). It arrives a moment into the clip instead, for
  // whoever has decided they do not want the rest. A second tap still leaves,
  // so nobody is held here either way.
  const [canSkip, setCanSkip] = useState(false);
  // how long the still sat there before the first tap: the door is watched,
  // but people take a moment to see that it wants one (owner, 2026-10-08)
  // (from when index.html drew it, if it did)
  const shownAt = useRef(pre ? Date.now() - (performance.now() - pre.drawnAt) : Date.now());
  // `null` is a door that wants no words on it, which is not the same as a door
  // that simply did not set one.
  const quote = door.quote === null ? null : (door.quote ?? DOOR_QUOTE);
  const leave = (how: 'ended' | 'skipped' | 'tapped') => {
    if (leaving) return;
    setLeaving(true);
    track('door_left', { how, at: Math.round(video.current?.currentTime ?? 0) });
    setTimeout(onDone, 450);
  };
  const play = () => {
    if (playing) return leave('tapped');
    const v = video.current;
    if (!v) return leave('skipped');
    setPlaying(true);
    track('door_played', { waitMs: Date.now() - shownAt.current });
    setTimeout(() => setCanSkip(true), SKIP_AFTER_MS);
    v.play().catch(() => leave('skipped'));
  };
  // the adopted video's own listener, kept on the latest render's `leave`
  const onEnded = useRef(() => {});
  onEnded.current = () => leave('ended');
  useLayoutEffect(() => {
    if (!pre || !slot.current) return;
    slot.current.appendChild(pre.media); // a move in one task: a playing clip does not pause
    finishPreDoor(pre);
    const media = pre.media;
    if (!(media instanceof HTMLVideoElement)) return;
    video.current = media;
    // the bundle is here, so the clip no longer competes with it: start it now, not at window load
    if (!media.getAttribute('src') && door.clip) media.src = door.clip;
    const ended = () => onEnded.current();
    const moved = () => setMoving(true);
    media.addEventListener('ended', ended);
    media.addEventListener('playing', moved);
    return () => { media.removeEventListener('ended', ended); media.removeEventListener('playing', moved); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre]);
  // what was done to the drawn door before the app was here
  useEffect(() => {
    if (!pre || pre.firstTapAt === null) return;
    if (!door.clip || pre.failed) { leave('skipped'); return; }
    track('door_played', { before_app: true, waitMs: Math.round(pre.firstTapAt - pre.drawnAt) });
    const v = video.current;
    if (v?.ended) { leave('ended'); return; }
    if (pre.taps > 1) { leave('tapped'); return; }
    const t = setTimeout(() => setCanSkip(true), Math.max(0, SKIP_AFTER_MS - (performance.now() - pre.firstTapAt)));
    return () => clearTimeout(t);
    // once, for the door as it was found
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pre]);
  return (
    <div
      className={`door ${quote ? '' : 'no-words'} ${playing && moving ? 'playing' : ''} ${leaving ? 'door-out' : ''}`}
      style={{ '--door-ground': door.ground, '--door-ink': door.ink, '--door-focus': door.focus ?? 'center', '--door-scrim': door.scrim } as CSSProperties}
      onClick={play}
      role="button"
      aria-label={door.clip ? 'Play' : 'Begin'}
    >
      {pre
        // the drawn door's own element goes in here; `contents` keeps it a child of .door for layout
        ? <div ref={slot} style={{ display: 'contents' }} />
        : door.clip
          ? <video ref={video} src={door.clip} poster={door.art} playsInline preload="auto" onPlaying={() => setMoving(true)} onEnded={() => leave('ended')} />
          : <img className="door-still" src={door.art} alt="" />}
      {quote
        ? (
          <div className="door-words">
            {/* a slash is a line break in verse, not a character to read out */}
            <p className="quote">“{quote.text.split(' / ').map((line, k) => <span key={line}>{k > 0 && <br />}{line}</span>)}”</p>
            <div className="quote-by">{quote.by}</div>
          </div>
        )
        : <div />}
      <div className="door-cta">
        {door.clip && <span className="play">{playing ? <span className="spinner" /> : '▶'}</span>}
        <span>{!door.clip ? 'Tap to begin' : playing ? 'Loading…' : 'Tap to play'}</span>
      </div>
      {canSkip && <button className="door-skip in" onClick={(e) => { e.stopPropagation(); leave('skipped'); }}>Skip</button>}
    </div>
  );
}

/** How long a clip may sit loading before the screen says so and offers the way on. */
const SLOW_AFTER_MS = 6000;

/**
 * Kraben's moment: a clip of him setting the first exercise, in his own voice —
 * the task, not an introduction (owner, 2026-09-18). If the clip is not there
 * (or will not play) this moves straight on rather than putting the same words
 * on the screen as text.
 *
 * `clip` is the fetch the intro started when it opened (lib/clip.ts). Until it
 * lands and the video is actually playing, the poster shows that something is
 * on its way, and a tap does not quietly skip him: on a slow connection a still
 * frame that swallowed taps looked broken (owner, 2026-10-08).
 */
function KrabenHello({ clip, onDone }: { clip: Promise<string | null>; onDone: () => void }) {
  const video = useRef<HTMLVideoElement>(null);
  const [src, setSrc] = useState<string | undefined>(undefined);
  const [leaving, setLeaving] = useState(false);
  // 'loading' is the clip not yet here or not yet moving. 'blocked' is the
  // iPhone: this clip has a voice track, and a phone refuses to autoplay sound
  // it was not asked for. The refusal used to be read as "the clip is missing"
  // and Kraben's only instruction was dropped in silence. 'ended' holds the last
  // frame for a moment and offers another look, because a clip that plays once
  // and vanishes is one you can blink and miss (interview 2026-09-18).
  const [phase, setPhase] = useState<'loading' | 'playing' | 'blocked' | 'ended'>('loading');
  const [slow, setSlow] = useState(false);
  const grace = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const leave = (how: 'ended' | 'skipped' | 'missing') => {
    if (leaving) return;
    clearTimeout(grace.current);
    setLeaving(true);
    track('kraben_hello', { how, ...(how === 'skipped' && phase === 'loading' ? { loading: true } : {}) });
    setTimeout(onDone, how === 'missing' ? 0 : 400);
  };
  const start = () => {
    const v = video.current;
    if (!v) return;
    clearTimeout(grace.current);
    setPhase('loading');
    v.currentTime = 0;
    // A refused play is a policy, not a fault: hold the poster and let the tap
    // that follows start it, which is a gesture the phone does allow.
    v.play().catch((e: Error) => {
      if (e?.name === 'AbortError') return;
      setPhase('blocked');
      track('kraben_hello', { how: 'blocked' });
    });
  };
  useEffect(() => {
    let live = true;
    clip.then((url) => { if (!live) return; if (url === null) leave('missing'); else setSrc(url); });
    const t = setTimeout(() => setSlow(true), SLOW_AFTER_MS);
    return () => { live = false; clearTimeout(t); clearTimeout(grace.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { if (src) start(); }, [src]);
  const ended = () => {
    setPhase('ended');
    grace.current = setTimeout(() => leave('ended'), REPLAY_GRACE_MS);
  };
  // a blob that will not decode gets one go at the network before he is dropped
  const failed = () => (src && src !== KRABEN_HELLO.clip ? setSrc(KRABEN_HELLO.clip) : leave('missing'));
  const tap = () => {
    if (phase === 'blocked') return start();
    if (phase === 'ended') return leave('ended');
    if (phase === 'playing') return leave('skipped');
    // loading: the tap is the gesture a phone wants for sound, so use it
    if (src) start();
  };
  return (
    <div
      className={`door kraben-hello ${phase} ${leaving ? 'door-out' : ''}`}
      onClick={tap}
      role="button"
      aria-label="Kraben sets the first exercise"
    >
      <video ref={video} src={src} poster={KRABEN_HELLO.poster} playsInline preload="auto" onPlaying={() => setPhase('playing')} onEnded={ended} onError={failed} />
      {phase === 'loading' && (
        <div className="door-cta clip-loading" aria-live="polite">
          <span className="play"><span className="spinner" /></span>
          <span>{slow ? 'Slow connection. Still loading…' : 'One moment…'}</span>
        </div>
      )}
      {phase === 'blocked' && (
        <div className="door-cta">
          <span className="play">▶</span>
          <span>Tap to hear him</span>
        </div>
      )}
      {phase === 'ended' && (
        <button className="door-again" onClick={(e) => { e.stopPropagation(); track('kraben_hello', { how: 'again' }); start(); }}>Watch again</button>
      )}
      {/* in every phase, including the refused one: a clip this phone will not
          play must not become a screen with no way off it */}
      <button className="door-skip in" onClick={(e) => { e.stopPropagation(); leave(phase === 'ended' ? 'ended' : 'skipped'); }}>{phase === 'ended' || slow ? 'Go on' : 'Skip'}</button>
    </div>
  );
}

function Becomes() {
  const [i, setI] = useState(0);
  useEffect(() => {
    BECOMES.forEach((b) => { const im = new Image(); im.src = b.art; });
    const id = setInterval(() => setI((k) => (k + 1) % BECOMES.length), 2600);
    return () => clearInterval(id);
  }, []);
  return (
    <>
      <div className="intro-art becomes">
        {BECOMES.map((b, k) => <img key={b.word} src={b.art} alt="" className={k === i ? 'on' : ''} />)}
      </div>
      <h1>Today’s idea is tomorrow’s <span key={BECOMES[i].word} className="swap">{BECOMES[i].word}.</span></h1>
    </>
  );
}

export function Intro() {
  const nav = useNavigate();
  const [first] = useState<Screen>(firstScreen);
  const [screen, setScreen] = useState<Screen>(first);
  const [i, setI] = useState(0);
  useViewed('intro', screen === 'panels' ? `panel_${i}` : screen);
  // No door: index.html has usually drawn this question already (lib/pre-quiz.ts),
  // and somebody may have answered it before the app was here. Likewise the road landing (lib/pre-road.ts).
  const [preQuiz] = useState(() => (first === 'survey' ? claimPreQuiz() : null));
  const [preRoad] = useState(() => (first === 'landing' ? claimPreRoad() : null));
  const [when, setWhen] = useState<string | null>(() => preQuiz?.when ?? null);
  const [dreams, setDreams] = useState<string[]>([]);

  // Which front door they came through is on every event already (`entry`, set
  // from the first-touch campaign record); this one says whether the door
  // screen was shown at all.
  const [door] = useState<DoorDef>(() => getDoor(loadCampaign()?.door));

  /**
   * The first activity, when an ad asked for one other than the warm-up
   * (`?start=imagine`, lib/starter.ts, well/product/33). Kraben's clip is left
   * out for it, and not fetched: in it he says "tell a story, just start
   * talking", which is the warm-up's instruction, not this one's.
   */
  const [starter] = useState(currentStarter);
  /**
   * The idea-step experiment: this browser's arm, and the dream the step asks
   * about — only for the imagine starter, whose drawing the idea becomes, and
   * none for "Not sure yet" alone or no answer. What they said lives here
   * while they go back and forth, and on the profile once said.
   */
  const [arm] = useState(() => ideaStep());
  const ideaFor = arm === 'on' && starter === 'imagine' ? dreamForIdea(dreams) : undefined;
  const [kind, setKind] = useState<{ for?: string; label: string }>(() => ({ label: '' }));
  const [about, setAbout] = useState('');
  /** After the two questions, always the cards. */
  const afterQuestions: Screen = 'panels';
  /**
   * The cards: all three, or for the imagine starter only the first ("Make one
   * small thing a day."), followed by the road itself in place of the other two
   * (components/FirstRoad.tsx, owner 2026-10-15): the promise said, then shown.
   */
  const cards = starter === 'imagine' ? PANELS.slice(0, 1) : PANELS;
  const panel = cards[i];
  const last = i === cards.length - 1;

  // `drawn_ms`: how long after the tap on the ad the question was on screen,
  // when index.html drew it (performance.now() is counted from the navigation).
  useEffect(() => {
    const drawn = preQuiz ?? preRoad;
    track('intro_door', { door: first === 'door' ? 'adult' : 'plush', ...(first === 'landing' ? { first: 'road' } : {}), ...(drawn ? { drawn_ms: Math.round(drawn.drawnAt) } : {}) });
  }, [first, preQuiz, preRoad]);
  // One event per screen seen, panels by index, so the whole intro is one
  // funnel in PostHog (`intro_step` broken down by `screen`) and the screen
  // somebody stopped on is the last one they have a row for.
  useEffect(() => { track('intro_step', { screen, ...(screen === 'panels' ? { panel: i } : {}) }); }, [screen, i]);
  useEffect(() => {
    if (screen === 'kind' && ideaFor) track('intro_kind_shown', { dream: ideaFor });
    if (screen === 'about' && ideaFor) track('intro_about_shown', { dream: ideaFor });
  }, [screen, ideaFor]);
  useEffect(() => { [...cards.map((p) => p.art), ...(starter === 'imagine' ? ['/art/road/road-bg.jpg'] : [])].forEach((src) => { if (src) { const im = new Image(); im.src = src; } }); }, [cards, starter]);

  /**
   * Kraben's clip is fetched from the moment the intro opens, while the
   * question and the cards are read, so it is usually here before it is wanted.
   * Nothing waits on it: "Start exercise 1" moves at once, and his screen shows
   * the clip on its way if it is not. A file that turns out not to be a video
   * (the app shipped without it) drops his screen, as before.
   */
  const [clip] = useState(() => (starter ? null : preloadClip(KRABEN_HELLO.clip, KRABEN_HELLO.poster)));

  /**
   * Both questions behind them, by whichever button: Next, "Not sure yet", or
   * Back-and-forward again. Sent once, because it is a Meta rung as well
   * (lib/meta-pixel.ts) — a ladder event to read, not the one to bid on.
   */
  const answeredOnce = useRef(false);
  const answered = (how: 'chosen' | 'not_sure') => {
    if (answeredOnce.current) return;
    answeredOnce.current = true;
    track('intro_answered', { when, dreams: how === 'chosen' ? dreams.length : 0, how });
  };

  /**
   * On into the first activity. "I don't have any" (`no_idea`) asks the
   * imagine starter for the Postcard Machine whatever its own experiment says:
   * the machine deals the idea with them.
   */
  const go = (how: 'finished' | 'skipped' | 'no_idea') => {
    saveProfile({ ...loadProfile(), introSeen: true });
    track('intro_done', { panels: how === 'finished' && screen !== 'landing' ? cards.length : 0, how, from: screen, idea: !!(ideaFor && ideaOf(loadProfile())) });
    nav(startPath(starter), { state: { door: 'front', ...(how === 'no_idea' ? { machine: true } : {}) } });
  };

  // The survey is on the page now, in the same place: the drawn one goes before
  // the next paint, and whatever was tapped on it counts as tapped here.
  const tookOver = useRef(false);
  useLayoutEffect(() => {
    if (!preQuiz || tookOver.current) return;
    tookOver.current = true;
    finishPreQuiz(preQuiz);
    if (preQuiz.when) {
      saveProfile({ ...loadProfile(), lastMade: preQuiz.when as 'recent' | 'ages' | 'never' });
      track('intro_when', { when: preQuiz.when, before_app: true });
    }
    if (preQuiz.skipped) go('skipped');
    // once, for the question as it was found
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preQuiz]);
  // The road landing, the same: its button tapped before the app was here is a tap on this one.
  const tookOverRoad = useRef(false);
  useLayoutEffect(() => {
    if (!preRoad || tookOverRoad.current) return;
    tookOverRoad.current = true;
    finishPreRoad(preRoad);
    if (preRoad.went) go('finished');
    // once, for the road as it was found
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preRoad]);

  if (screen === 'landing') return <FirstRoad promise={door.promise} onGo={() => go('finished')} />;

  if (screen === 'door') return <AdultDoor door={door} onDone={() => setScreen('survey')} />;

  if (screen === 'survey') {
    const chosen = WHEN.find((w) => w.id === when);
    return (
      <Frame theme="fit" left={<span className="tag">Day 1</span>}>
        <div className="intro">
          <div className="intro-kraben pop" style={{ height: 'min(18dvh, 150px)' }}><KrabenMark size={110} /></div>
          <p className="promise">{door.promise}</p>
          <h1>{QUESTION.title}</h1>
          <div className="big-opts">
            {WHEN.map((w) => <button key={w.id} className={`big-opt ${when === w.id ? 'on' : ''}`} onClick={() => { setWhen(w.id); saveProfile({ ...loadProfile(), lastMade: w.id as 'recent' | 'ages' | 'never' }); track('intro_when', { when: w.id }); }}>{w.label}</button>)}
          </div>
          {chosen && <p key={chosen.id} className="why swap" style={{ fontSize: 17 }}>{chosen.reply}</p>}
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          {chosen && <button className="btn coral" onClick={() => setScreen('dream')}>Next</button>}
          <div className="row" style={{ justifyContent: 'center' }}><button className="link" onClick={() => go('skipped')}>Skip to the exercise</button></div>
        </div>
      </Frame>
    );
  }

  if (screen === 'dream') {
    const toggle = (id: string) => setDreams(dreams.includes(id) ? dreams.filter((x) => x !== id) : [...dreams, id]);
    const keep = () => {
      // no idea step for these dreams: an idea from an earlier pass is not kept, so the starter asks for a place
      saveProfile({ ...loadProfile(), dreams, dream: dreams[0], ...(ideaFor ? {} : { dreamKind: undefined, dreamAbout: undefined }) });
      track('intro_dream', { dreams, count: dreams.length });
      answered('chosen');
      setScreen(ideaFor ? 'kind' : afterQuestions);
    };
    return (
      <Frame theme="fit" left={<span className="tag">Day 1</span>}>
        <div className="intro">
          <h1>{DREAM.title}</h1>
          <p className="why">{DREAM.body}</p>
          <div className="big-opts two">
            {DREAMS.map((d) => <button key={d.id} className={`big-opt ${dreams.includes(d.id) ? 'on' : ''}`} onClick={() => toggle(d.id)} aria-pressed={dreams.includes(d.id)}>{d.label}</button>)}
          </div>
        </div>
        <div className="stack" style={{ marginTop: 'auto' }}>
          {dreams.length > 0 && <button className="btn coral" onClick={keep}>Next</button>}
          <div className="row" style={{ justifyContent: 'center', gap: 18 }}>
            <button className="link" onClick={() => setScreen('survey')}>Back</button>
            {dreams.length === 0 && <button className="link" onClick={() => { answered('not_sure'); const p = loadProfile(); if (p.dreamKind || p.dreamAbout) saveProfile({ ...p, dreamKind: undefined, dreamAbout: undefined }); setScreen(afterQuestions); }}>Not sure yet</button>}
          </div>
        </div>
      </Frame>
    );
  }

  if (screen === 'kind' && ideaFor) {
    return (
      <KindStep
        dream={ideaFor}
        chosen={kind.for === ideaFor ? kind.label : ''}
        onPick={(label) => { setKind({ for: ideaFor, label }); saveProfile({ ...loadProfile(), dreamKind: label }); setScreen('about'); }}
        onBack={() => setScreen('dream')}
      />
    );
  }

  if (screen === 'about' && ideaFor) {
    return (
      <AboutStep
        dream={ideaFor}
        kind={kind.label}
        about={about}
        setAbout={setAbout}
        onDone={(text) => { saveProfile({ ...loadProfile(), dreamKind: kind.label, dreamAbout: text }); setI(0); setScreen(afterQuestions); }}
        // their kind is kept; with no idea the starter asks for a place, on the Postcard Machine, straight away
        onNone={() => { saveProfile({ ...loadProfile(), dreamKind: kind.label, dreamAbout: undefined }); go('no_idea'); }}
        onBack={() => setScreen('kind')}
      />
    );
  }

  if (screen === 'road') return <FirstRoad onGo={() => go('finished')} onBack={() => setScreen('panels')} />;

  if (screen === 'kraben' && clip) return <KrabenHello clip={clip} onDone={() => go('finished')} />;

  return (
    <Frame theme="fit" left={<span className="tag">Day 1</span>}>
      <div className="intro">
        {panel.rotate ? <Becomes /> : (
          <>
            {panel.art ? <img className="intro-art" src={panel.art} alt="" style={{ objectPosition: panel.pos }} /> : <div className="intro-kraben"><KrabenMark size={150} /></div>}
            <h1>{panel.title}</h1>
          </>
        )}
        {panel.body && <p className="why" style={{ fontSize: 17 }}>{panel.body}</p>}
        {panel.quoteBy && <span className="hint">{panel.quoteBy}</span>}
      </div>
      <div className="stack" style={{ marginTop: 'auto' }}>
        {cards.length > 1 && <div className="dots" style={{ justifyContent: 'center' }}>{cards.map((_, k) => <span key={k} className={`dot ${k <= i ? '' : 'off'}`} />)}</div>}
        {/* the imagine starter's one card goes on to the road, which starts the exercise itself */}
        <button className="btn coral" onClick={() => (last ? (starter === 'imagine' ? setScreen('road') : clip ? setScreen('kraben') : go('finished')) : setI(i + 1))}>{last && starter !== 'imagine' ? 'Start exercise 1' : 'Next'}</button>
        <div className="row" style={{ justifyContent: 'center' }}>
          <button className="link" onClick={() => (i === 0 ? setScreen(ideaFor ? 'about' : 'dream') : setI(i - 1))}>Back</button>
        </div>
      </div>
    </Frame>
  );
}
